import "./App.css";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import ProtectedRoute from "./ProtectedRoute";
import Layout from "./components/Layout";

import { Center, Container, Heading, Text } from "@chakra-ui/react";
import CustomButton1 from "./components/CustomButton1";
import CustomPopup from "./components/CustomPopup";
import store from "./redux/Store";
import { setSessionOut, setUser_id } from "./redux/slice/userSlice";
import { getDToken, onMessageListener } from "./firebase";
import { getNotifications } from "./redux/slice/notificationSlice";
import { NotificationManager } from "react-notifications";
import { get_background_image, get_video } from "./redux/slice/OtherSlice";
import Loader2 from "./components/Loader2";

// Lazy-loaded screen components — each becomes a separate JS chunk (code splitting)
const Singup = React.lazy(() => import("./screens/Singup"));
const Addpolicy = React.lazy(() => import("./screens/policy/Addpolicy"));
const Adddocument = React.lazy(() => import("./screens/documents/Adddocument "));
const Notificationlist = React.lazy(() => import("./screens/Notificationlist"));
const Login = React.lazy(() => import("./screens/Login"));
const Home = React.lazy(() => import("./screens/home/Home"));
const Editpolicy = React.lazy(() => import("./screens/policy/EditPolicy"));
const EditDocument = React.lazy(() => import("./screens/documents/EditDocument"));
const Profile = React.lazy(() => import("./screens/setting/Profile"));
const AskanExpert = React.lazy(() => import("./screens/setting/AskanExpert"));
const Claims = React.lazy(() => import("./screens/setting/Claims"));
const BuyPolicy = React.lazy(() => import("./screens/setting/BuyPolicy"));
const NotificationDetail = React.lazy(() => import("./screens/NotificationDetail"));
const ArchivedPolicy = React.lazy(() => import("./screens/setting/ArchivedPolicy"));
const ArchivedDocument = React.lazy(() => import("./screens/setting/ArchivedDocument"));
const ResetPassword = React.lazy(() => import("./screens/ResetPassword"));
const Documents = React.lazy(() => import("./screens/home/Documents"));
const Policy = React.lazy(() => import("./screens/home/Policy"));
const DeleteAccount = React.lazy(() => import("./screens/DeleteAccount"));
const RedirectScreen = React.lazy(() => import("./screens/RedirectScreen"));

const SESSION_TIMEOUT_MS = 600_000; // 10 minutes

function App() {
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(window.navigator.onLine);
  const user = JSON.parse(localStorage.getItem("user"));
  const location = useLocation();
  const pathname = location?.pathname;

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    store.dispatch(setUser_id(storedUser?.userid));
    store.dispatch(get_background_image());
    store.dispatch(get_video());
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const handleFocus = () => {
      store.dispatch(getNotifications({ id: user?.userid }));
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [user?.userid]);

  useEffect(() => {
    const handleStatusChange = () => setIsOnline(window.navigator.onLine);
    window.addEventListener("online", handleStatusChange);
    window.addEventListener("offline", handleStatusChange);
    return () => {
      window.removeEventListener("online", handleStatusChange);
      window.removeEventListener("offline", handleStatusChange);
    };
  }, []);

  useEffect(() => {
    const check = performance.getEntriesByType("navigation")[0]?.type;
    if (check !== "reload") {
      localStorage.removeItem("user");
      localStorage.removeItem("fbToken");
    }
  }, []);

  // ─── Session Timeout ────────────────────────────────────────────────────────
  // Single setTimeout fires once after inactivity — no setInterval re-renders.
  const [sessionExpire, setSessionExpire] = useState(false);
  const timerRef = useRef(null);
  const lastActivityRef = useRef(0);

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      if (localStorage.getItem("user") !== null) {
        localStorage.clear();
        setSessionExpire(true);
      }
    }, SESSION_TIMEOUT_MS);
  }, []);

  // Throttled to max once per 500ms — avoids flooding on every mouse pixel move
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current > 500) {
      lastActivityRef.current = now;
      resetTimer();
    }
  }, [resetTimer]);

  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      resetTimer();
    } else {
      clearTimeout(timerRef.current);
    }
  }, [resetTimer]);

  useEffect(() => {
    resetTimer();
    document.addEventListener("keydown", handleUserActivity);
    document.addEventListener("mousemove", handleUserActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("keydown", handleUserActivity);
      document.removeEventListener("mousemove", handleUserActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(timerRef.current);
    };
  }, [handleUserActivity, handleVisibilityChange, resetTimer]);

  // ─── Firebase Push Notifications ────────────────────────────────────────────
  useEffect(() => {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        getDToken();
      }
    });
  }, []);

  useEffect(() => {
    let active = true;
    const listen = () => {
      onMessageListener()
        .then((payload) => {
          if (!active) return;
          if (user?.userid) {
            store.dispatch(getNotifications({ id: user.userid }));
          }
          NotificationManager.success(
            payload.notification.body,
            payload.notification.title,
            2000,
            () => navigate("/all-notifications")
          );
          listen(); // re-subscribe for next message
        })
        .catch(() => {});
    };
    listen();
    return () => {
      active = false;
    };
  }, [user?.userid, navigate]);

  return (
    <>
      {isOnline ? (
        <Suspense fallback={<Loader2 />}>
          <Routes>
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Layout />}>
                <Route index element={<Home />} />
                <Route path="policy" element={<Policy />} />
                <Route path="add-policy" element={<Addpolicy />} />
                <Route path="edit-policy" element={<Editpolicy />} />
                <Route path="document" element={<Documents />} />
                <Route path="add-document" element={<Adddocument />} />
                <Route path="edit-document" element={<EditDocument />} />
                <Route path="all-notifications" element={<Notificationlist />} />
                <Route path="notification-detail" element={<NotificationDetail />} />
                <Route path="profile" element={<Profile />} />
                <Route path="ask-an-expert" element={<AskanExpert />} />
                <Route path="claims" element={<Claims />} />
                <Route path="buy-policy" element={<BuyPolicy />} />
                <Route path="archived-policies" element={<ArchivedPolicy />} />
                <Route path="archived-documents" element={<ArchivedDocument />} />
              </Route>
            </Route>
            <Route path="login" element={<Login />} />
            <Route path="redirect" element={<RedirectScreen />} />
            <Route path="singup" element={<Singup />} />
            <Route path="delete-account" element={<DeleteAccount />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="/404" element={<PageNotFound />} />
            <Route path="*" element={<Navigate to="/404" />} />
          </Routes>
        </Suspense>
      ) : (
        <NoInternet />
      )}
      <CustomPopup
        isOpen={sessionExpire}
        onClose={() => {
          setSessionExpire(false);
          localStorage.clear();
          window.sessionStorage.clear();
          store.dispatch(setSessionOut(false));
          store.dispatch(setUser_id(""));
          window.location.reload();
        }}
        title={"Alert"}
        mess={"Your session is expired please Login"}
        single_button={true}
        onClick={() => {
          setSessionExpire(false);
          window.sessionStorage.clear();
          localStorage.clear();
          store.dispatch(setSessionOut(false));
          store.dispatch(setUser_id(""));
          window.location.reload();
        }}
        b_name={"OK"}
      />
    </>
  );
}

function NoInternet() {
  return (
    <Container
      bg={"#f8f9fe"}
      display={"flex"}
      h={"100vh"}
      maxW={"100vw"}
      alignItems={"center"}
      justifyContent={"center"}
      flexDirection={"column"}
    >
      <Heading color={"#e0a604"}>No Internet Connection</Heading>
      <Text my={4}>Please check your connection</Text>
      <Center>
        <CustomButton1
          title={"Try Again"}
          onClick={() => window.location.reload()}
        />
      </Center>
    </Container>
  );
}

function PageNotFound() {
  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <Heading fontSize={"6xl"} color={"#e0a604"}>
          404
        </Heading>
        <Heading fontSize={"2xl"} paddingBlock={2}>
          Page Not Found
        </Heading>
        <Text>Oops! You seem to be lost.</Text>
        <Text>
          Go to
          <Link
            to="/"
            style={{ textDecorationLine: "underline", padding: 5 }}
          >
            {" "}Home{" "}
          </Link>
          page
        </Text>
      </div>
    </div>
  );
}

export default App;
